import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { LOGO_URL } from "@/lib/constants";

const BrandGuidelinesPage = () => {
  useEffect(() => {
    document.title = "Brand Guidelines | Digital Media Ninja";
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Brand Guidelines | Digital Media Ninja</title>
        <meta name="description" content="Official brand guidelines for Digital Media Ninja. Reference for freelancers, partners, and internal team members." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        .bg-page { --teal:#2ABFBF; --teal-dark:#1E9999; --charcoal:#2C3140; --slate:#3D4557; --off-white:#F7F8FA; --bg-white:#FFFFFF; --bg-black:#0A0A0A; --mid-gray:#8492A6; --light-gray:#E8ECF2; }
        .bg-page { font-family:'Inter',sans-serif; background:var(--bg-white); color:var(--charcoal); line-height:1.65; font-size:16px; }
        .bg-page *, .bg-page *::before, .bg-page *::after { box-sizing:border-box; }
        .bg-cover { background:var(--bg-black); min-height:100vh; display:flex; flex-direction:column; justify-content:center; align-items:center; text-align:center; padding:60px 40px; position:relative; overflow:hidden; }
        .bg-cover::before { content:''; position:absolute; width:600px; height:600px; border-radius:50%; background:radial-gradient(circle,rgba(42,191,191,0.12) 0%,transparent 70%); top:-100px; right:-100px; }
        .bg-cover::after { content:''; position:absolute; width:400px; height:400px; border-radius:50%; background:radial-gradient(circle,rgba(42,191,191,0.07) 0%,transparent 70%); bottom:-80px; left:-80px; }
        .bg-cover-logo { width:320px; height:120px; background:var(--charcoal); border:2px dashed var(--teal); border-radius:8px; display:flex; align-items:center; justify-content:center; color:var(--teal); font-size:13px; letter-spacing:1px; margin-bottom:48px; position:relative; z-index:1; font-family:'Space Grotesk',sans-serif; }
        .bg-cover-eyebrow { color:var(--teal); font-size:12px; font-weight:600; letter-spacing:3px; text-transform:uppercase; margin-bottom:16px; position:relative; z-index:1; font-family:'Space Grotesk',sans-serif; }
        .bg-cover h1 { font-family:'Space Grotesk',sans-serif; font-size:clamp(36px,6vw,64px); font-weight:700; color:#fff; line-height:1.1; margin-bottom:20px; position:relative; z-index:1; }
        .bg-cover h1 span { color:var(--teal); }
        .bg-cover-sub { font-size:18px; color:var(--mid-gray); max-width:480px; margin-bottom:48px; position:relative; z-index:1; }
        .bg-cover-meta { display:flex; gap:32px; position:relative; z-index:1; flex-wrap:wrap; justify-content:center; }
        .bg-cover-meta span { font-size:12px; color:var(--mid-gray); letter-spacing:1px; text-transform:uppercase; }
        .bg-cover-meta span strong { color:var(--teal); }
        .bg-nav { position:sticky; top:0; z-index:100; background:rgba(10,10,10,0.96); backdrop-filter:blur(10px); display:flex; gap:0; overflow-x:auto; border-bottom:1px solid rgba(42,191,191,0.2); }
        .bg-nav a { color:var(--mid-gray); text-decoration:none; font-size:12px; font-weight:600; letter-spacing:1px; text-transform:uppercase; padding:14px 20px; white-space:nowrap; transition:color 0.2s,border-bottom 0.2s; border-bottom:2px solid transparent; font-family:'Space Grotesk',sans-serif; }
        .bg-nav a:hover { color:var(--teal); border-bottom-color:var(--teal); }
        .bg-container { max-width:1100px; margin:0 auto; padding:0 40px; }
        .bg-section { padding:80px 0; border-bottom:1px solid var(--light-gray); }
        .bg-section:last-child { border-bottom:none; }
        .bg-eyebrow { font-size:11px; font-weight:700; letter-spacing:3px; text-transform:uppercase; color:var(--teal); margin-bottom:10px; font-family:'Space Grotesk',sans-serif; }
        .bg-section-title { font-family:'Space Grotesk',sans-serif; font-size:clamp(28px,4vw,42px); font-weight:700; color:var(--bg-black); margin-bottom:16px; line-height:1.15; }
        .bg-section-intro { font-size:17px; color:var(--mid-gray); max-width:620px; margin-bottom:52px; line-height:1.7; }
        .bg-about-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
        .bg-about-card { background:var(--off-white); border-radius:12px; padding:28px 32px; border-left:4px solid var(--teal); }
        .bg-about-card h3 { font-family:'Space Grotesk',sans-serif; font-size:15px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:var(--charcoal); margin-bottom:10px; }
        .bg-about-card p { font-size:15px; color:#555; line-height:1.65; }
        .bg-colors-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:20px; margin-bottom:40px; }
        .bg-color-swatch { border-radius:12px; overflow:hidden; box-shadow:0 2px 16px rgba(0,0,0,0.08); }
        .bg-swatch-block { height:120px; }
        .bg-swatch-info { padding:16px 20px; background:var(--bg-white); border:1px solid var(--light-gray); border-top:none; border-radius:0 0 12px 12px; }
        .bg-swatch-info h4 { font-family:'Space Grotesk',sans-serif; font-size:14px; font-weight:700; color:var(--charcoal); margin-bottom:6px; }
        .bg-swatch-info .bg-hex { font-family:monospace; font-size:13px; color:var(--mid-gray); display:block; margin-bottom:2px; }
        .bg-swatch-info .bg-rgb { font-family:monospace; font-size:12px; color:var(--mid-gray); }
        .bg-usage-tag { display:inline-block; margin-top:10px; background:var(--off-white); border-radius:100px; padding:3px 10px; font-size:11px; font-weight:600; color:var(--charcoal); text-transform:uppercase; letter-spacing:0.5px; }
        .bg-type-samples { display:flex; flex-direction:column; gap:32px; }
        .bg-type-card { background:var(--off-white); border-radius:12px; padding:36px 40px; border:1px solid var(--light-gray); }
        .bg-type-label { font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:var(--teal); margin-bottom:12px; font-family:'Space Grotesk',sans-serif; }
        .bg-type-spec { font-size:12px; color:var(--mid-gray); margin-top:12px; font-family:monospace; padding-top:12px; border-top:1px solid var(--light-gray); }
        .bg-logo-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-bottom:40px; }
        .bg-logo-card { border-radius:12px; padding:40px; display:flex; flex-direction:column; gap:12px; min-height:180px; justify-content:center; }
        .bg-logo-card.dark-bg { background:var(--bg-black); }
        .bg-logo-card.light-bg { background:var(--off-white); border:1px solid var(--light-gray); }
        .bg-logo-card.teal-bg { background:var(--teal); }
        .bg-logo-card .bg-logo-label { font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase; font-family:'Space Grotesk',sans-serif; }
        .bg-logo-card.dark-bg .bg-logo-label { color:var(--mid-gray); }
        .bg-logo-card.light-bg .bg-logo-label { color:var(--mid-gray); }
        .bg-logo-card.teal-bg .bg-logo-label { color:rgba(255,255,255,0.7); }
        .bg-logo-card .bg-logo-text { font-family:'Space Grotesk',sans-serif; font-weight:700; font-size:22px; }
        .bg-logo-card.dark-bg .bg-logo-text { color:#fff; }
        .bg-logo-card.light-bg .bg-logo-text { color:var(--charcoal); }
        .bg-logo-card.teal-bg .bg-logo-text { color:#fff; }
        .bg-logo-card .bg-logo-name { font-size:13px; color:var(--mid-gray); margin-top:4px; }
        .bg-donts-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:16px; }
        .bg-dont-card { background:#FFF5F5; border-radius:10px; padding:20px 22px; border-left:4px solid #E85555; }
        .bg-dont-card h4 { font-size:14px; font-weight:700; color:#C0392B; margin-bottom:6px; font-family:'Space Grotesk',sans-serif; }
        .bg-dont-card p { font-size:13px; color:#666; }
        .bg-voice-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:20px; margin-bottom:40px; }
        .bg-voice-card { background:var(--bg-white); border:1px solid var(--light-gray); border-radius:12px; padding:28px; position:relative; overflow:hidden; }
        .bg-voice-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:var(--teal); }
        .bg-voice-card .bg-voice-trait { font-family:'Space Grotesk',sans-serif; font-size:18px; font-weight:700; color:var(--charcoal); margin-bottom:8px; }
        .bg-voice-card p { font-size:14px; color:#666; line-height:1.6; }
        .bg-voice-compare { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
        .bg-vc-card { border-radius:10px; padding:24px; }
        .bg-vc-card.do { background:#F0FBF8; border:1px solid #A3D9CB; }
        .bg-vc-card.dont { background:#FFF5F5; border:1px solid #F5C6CB; }
        .bg-vc-card h4 { font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:1px; margin-bottom:12px; }
        .bg-vc-card.do h4 { color:#1A8A6A; }
        .bg-vc-card.dont h4 { color:#C0392B; }
        .bg-vc-card ul { list-style:none; display:flex; flex-direction:column; gap:8px; padding:0; }
        .bg-vc-card li { font-size:14px; color:#444; padding-left:20px; position:relative; line-height:1.5; }
        .bg-vc-card.do li::before { content:'\\2713'; position:absolute; left:0; color:#1A8A6A; font-weight:700; }
        .bg-vc-card.dont li::before { content:'\\2717'; position:absolute; left:0; color:#C0392B; font-weight:700; }
        .bg-services-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:20px; }
        .bg-service-card { background:var(--bg-white); border:1px solid var(--light-gray); border-radius:12px; padding:28px 32px; transition:box-shadow 0.2s; }
        .bg-service-card:hover { box-shadow:0 8px 32px rgba(42,191,191,0.12); }
        .bg-service-icon { width:44px; height:44px; background:rgba(42,191,191,0.1); border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:22px; margin-bottom:16px; }
        .bg-service-card h3 { font-family:'Space Grotesk',sans-serif; font-size:17px; font-weight:700; color:var(--charcoal); margin-bottom:8px; }
        .bg-service-card p { font-size:14px; color:#666; line-height:1.65; }
        .bg-persona-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:24px; }
        .bg-persona-card { background:var(--off-white); border-radius:14px; overflow:hidden; border:1px solid var(--light-gray); }
        .bg-persona-header { background:var(--charcoal); padding:24px 28px; display:flex; align-items:center; gap:16px; }
        .bg-persona-avatar { width:48px; height:48px; background:var(--teal); border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
        .bg-persona-name { font-family:'Space Grotesk',sans-serif; font-size:17px; font-weight:700; color:#fff; }
        .bg-persona-role { font-size:12px; color:var(--teal); font-weight:600; }
        .bg-persona-body { padding:24px 28px; }
        .bg-persona-body h4 { font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:var(--teal); margin-top:16px; margin-bottom:6px; }
        .bg-persona-body h4:first-child { margin-top:0; }
        .bg-persona-body p, .bg-persona-body li { font-size:14px; color:#555; line-height:1.6; }
        .bg-persona-body ul { list-style:disc; padding-left:18px; display:flex; flex-direction:column; gap:4px; }
        .bg-footer-inner { background:var(--bg-black); padding:48px 40px; text-align:center; color:var(--mid-gray); font-size:13px; }
        .bg-footer-inner strong { color:var(--teal); }
        .bg-tag { display:inline-block; background:rgba(42,191,191,0.1); color:var(--teal); border-radius:100px; padding:4px 12px; font-size:12px; font-weight:600; letter-spacing:0.5px; margin:3px 2px; }
        .bg-divider { height:1px; background:var(--light-gray); margin:40px 0; }
        @media (max-width:768px) {
          .bg-container { padding:0 20px; }
          .bg-section { padding:56px 0; }
          .bg-about-grid, .bg-logo-grid, .bg-voice-compare { grid-template-columns:1fr; }
          .bg-cover h1 { font-size:36px; }
        }
      `}</style>

      <div className="bg-page" data-testid="brand-guidelines-page">
        {/* COVER */}
        <div className="bg-cover">
          <img src={LOGO_URL} alt="Digital Media Ninja" style={{height:"120px", width:"auto", marginBottom:"48px", position:"relative", zIndex:1}} />
          <div className="bg-cover-eyebrow">Official Brand Guidelines</div>
          <h1>The <span>Digital Media Ninja</span><br/>Brand Standard</h1>
          <p className="bg-cover-sub">A complete reference for freelancers, partners, and internal team members on how to represent the Digital Media Ninja brand consistently and correctly.</p>
          <div className="bg-cover-meta">
            <span><strong>Version</strong> 1.0</span>
            <span><strong>Updated</strong> April 2026</span>
            <span><strong>Agency</strong> Digital Media Ninja</span>
            <span><strong>Location</strong> Dallas, TX</span>
          </div>
        </div>

        {/* NAV */}
        <nav className="bg-nav">
          <a href="#about">About</a>
          <a href="#colors">Colors</a>
          <a href="#typography">Typography</a>
          <a href="#logo">Logo Usage</a>
          <a href="#voice">Voice &amp; Tone</a>
          <a href="#services">Services</a>
          <a href="#personas">Audience</a>
        </nav>

        {/* 01 ABOUT */}
        <div className="bg-section" id="about">
          <div className="bg-container">
            <div className="bg-eyebrow">01 — Who We Are</div>
            <h2 className="bg-section-title">The Digital Media Ninja Story</h2>
            <p className="bg-section-intro">Digital Media Ninja is a Dallas-based SEO and digital marketing agency pioneering the next generation of search visibility — from traditional SEO to AI-powered Answer Engine Optimization (AEO) and Generative Engine Optimization (GEO).</p>
            <div className="bg-about-grid">
              <div className="bg-about-card">
                <h3>Mission</h3>
                <p>To help businesses get found everywhere that matters — in Google, in AI-generated answers, and in the minds of their ideal customers. We cut through the digital noise so clients don't just rank, they dominate.</p>
              </div>
              <div className="bg-about-card">
                <h3>Positioning</h3>
                <p>We are early pioneers in AEO/GEO strategy, sitting at the intersection of traditional SEO mastery and emerging AI search optimization. We operate where most agencies haven't looked yet.</p>
              </div>
              <div className="bg-about-card">
                <h3>Founder</h3>
                <p>Led by Daniel Rivas (Ninja), a Certified Digital Marketing Professional (CDMP) based in Dallas, TX. Athlete, strategist, and builder — Daniel brings the same precision to client campaigns that he brings to HYROX competition.</p>
              </div>
              <div className="bg-about-card">
                <h3>Brand Personality</h3>
                <p>Strategic. Sharp. Credible without being corporate. We're the expert in the room who actually explains what's happening — no fluff, no vanity metrics, just clear results-driven thinking.</p>
              </div>
            </div>
          </div>
        </div>

        {/* 02 COLORS */}
        <div className="bg-section" id="colors">
          <div className="bg-container">
            <div className="bg-eyebrow">02 — Brand Colors</div>
            <h2 className="bg-section-title">Color System</h2>
            <p className="bg-section-intro">Our palette is built around a bold teal primary and a dark charcoal/black foundation — confident, modern, and technical without being cold.</p>
            <div className="bg-colors-grid">
              <div className="bg-color-swatch">
                <div className="bg-swatch-block" style={{background:"#2ABFBF"}} />
                <div className="bg-swatch-info">
                  <h4>Ninja Teal</h4>
                  <span className="bg-hex">#2ABFBF</span>
                  <span className="bg-rgb">RGB: 42, 191, 191</span>
                  <div className="bg-usage-tag">Primary Brand</div>
                </div>
              </div>
              <div className="bg-color-swatch">
                <div className="bg-swatch-block" style={{background:"#1E9999"}} />
                <div className="bg-swatch-info">
                  <h4>Deep Teal</h4>
                  <span className="bg-hex">#1E9999</span>
                  <span className="bg-rgb">RGB: 30, 153, 153</span>
                  <div className="bg-usage-tag">Hover / Active</div>
                </div>
              </div>
              <div className="bg-color-swatch">
                <div className="bg-swatch-block" style={{background:"#0A0A0A"}} />
                <div className="bg-swatch-info">
                  <h4>Ninja Black</h4>
                  <span className="bg-hex">#0A0A0A</span>
                  <span className="bg-rgb">RGB: 10, 10, 10</span>
                  <div className="bg-usage-tag">Backgrounds</div>
                </div>
              </div>
              <div className="bg-color-swatch">
                <div className="bg-swatch-block" style={{background:"#2C3140"}} />
                <div className="bg-swatch-info">
                  <h4>Charcoal</h4>
                  <span className="bg-hex">#2C3140</span>
                  <span className="bg-rgb">RGB: 44, 49, 64</span>
                  <div className="bg-usage-tag">Secondary BG</div>
                </div>
              </div>
              <div className="bg-color-swatch">
                <div className="bg-swatch-block" style={{background:"#FFFFFF", border:"1px solid #E8ECF2"}} />
                <div className="bg-swatch-info">
                  <h4>Pure White</h4>
                  <span className="bg-hex">#FFFFFF</span>
                  <span className="bg-rgb">RGB: 255, 255, 255</span>
                  <div className="bg-usage-tag">Content BG</div>
                </div>
              </div>
              <div className="bg-color-swatch">
                <div className="bg-swatch-block" style={{background:"#8492A6"}} />
                <div className="bg-swatch-info">
                  <h4>Slate Gray</h4>
                  <span className="bg-hex">#8492A6</span>
                  <span className="bg-rgb">RGB: 132, 146, 166</span>
                  <div className="bg-usage-tag">Body Text / UI</div>
                </div>
              </div>
            </div>
            <div className="bg-about-card" style={{marginTop:"8px"}}>
              <h3>Color Usage Rules</h3>
              <p>Ninja Teal (#2ABFBF) is always the primary action and accent color. On dark backgrounds (black or charcoal), use white for body copy and teal for headlines or highlights. On white/light backgrounds, use charcoal for body copy and teal for accents only — never use white text on white backgrounds. Never use teal as a large background fill behind teal text. Maintain WCAG AA contrast ratios at all times.</p>
            </div>
          </div>
        </div>

        {/* 03 TYPOGRAPHY */}
        <div className="bg-section" id="typography">
          <div className="bg-container">
            <div className="bg-eyebrow">03 — Typography</div>
            <h2 className="bg-section-title">Type System</h2>
            <p className="bg-section-intro">Our type system balances technical authority with readability. Space Grotesk powers our brand display moments; Inter keeps body copy clean and scannable at any size.</p>
            <div className="bg-type-samples">
              <div className="bg-type-card">
                <div className="bg-type-label">Display / Headlines — Space Grotesk Bold</div>
                <div style={{fontFamily:"'Space Grotesk',sans-serif", fontSize:"42px", fontWeight:700, color:"#0A0A0A", lineHeight:1.1}}>Get Found. Stay Found.</div>
                <div className="bg-type-spec">font-family: 'Space Grotesk' &middot; weight: 700 &middot; size: 32–64px &middot; color: #0A0A0A (light bg) or #FFFFFF (dark bg)</div>
              </div>
              <div className="bg-type-card">
                <div className="bg-type-label">Section Titles — Space Grotesk SemiBold</div>
                <div style={{fontFamily:"'Space Grotesk',sans-serif", fontSize:"28px", fontWeight:600, color:"#2C3140", lineHeight:1.2}}>SEO, AEO &amp; AI Search Optimization</div>
                <div className="bg-type-spec">font-family: 'Space Grotesk' &middot; weight: 600 &middot; size: 22–32px &middot; color: #2C3140</div>
              </div>
              <div className="bg-type-card">
                <div className="bg-type-label">Eyebrow / Labels — Space Grotesk, All-Caps</div>
                <div style={{fontFamily:"'Space Grotesk',sans-serif", fontSize:"12px", fontWeight:700, letterSpacing:"3px", textTransform:"uppercase", color:"#2ABFBF"}}>Dallas, TX &middot; Digital Marketing Agency</div>
                <div className="bg-type-spec">font-family: 'Space Grotesk' &middot; weight: 700 &middot; size: 10–13px &middot; letter-spacing: 2–4px &middot; UPPERCASE &middot; color: #2ABFBF</div>
              </div>
              <div className="bg-type-card">
                <div className="bg-type-label">Body Copy — Inter Regular</div>
                <div style={{fontFamily:"'Inter',sans-serif", fontSize:"16px", fontWeight:400, color:"#555", lineHeight:1.7, maxWidth:"680px"}}>We help businesses rank in traditional search and get cited in AI-generated answers. Our data-driven strategies are built for 2026 and beyond — where your next customer might find you through Google, ChatGPT, or Perplexity.</div>
                <div className="bg-type-spec">font-family: 'Inter' &middot; weight: 400 &middot; size: 15–17px &middot; line-height: 1.65–1.75 &middot; color: #555 or #666</div>
              </div>
              <div className="bg-type-card">
                <div className="bg-type-label">UI / Buttons / Captions — Inter Medium</div>
                <div style={{display:"flex", gap:"16px", alignItems:"center", flexWrap:"wrap"}}>
                  <span style={{background:"#2ABFBF", color:"#fff", fontFamily:"'Inter',sans-serif", fontSize:"14px", fontWeight:600, padding:"10px 24px", borderRadius:"6px", letterSpacing:"0.3px"}}>Get a Free SEO Audit</span>
                  <span style={{background:"transparent", color:"#2ABFBF", fontFamily:"'Inter',sans-serif", fontSize:"14px", fontWeight:600, padding:"10px 24px", borderRadius:"6px", border:"2px solid #2ABFBF"}}>View Services</span>
                  <span style={{fontFamily:"'Inter',sans-serif", fontSize:"12px", fontWeight:500, color:"#8492A6"}}>Caption or metadata text</span>
                </div>
                <div className="bg-type-spec">font-family: 'Inter' &middot; weight: 500–600 &middot; size: 12–15px &middot; buttons use full uppercase or sentence case</div>
              </div>
            </div>
            <div className="bg-divider" />
            <div className="bg-about-card">
              <h3>Web Font Fallbacks</h3>
              <p>Primary: <code>Space Grotesk</code> (Google Fonts) &rarr; Fallback: <code>-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif</code>. For documents and presentations where web fonts aren't available, substitute <strong>Montserrat Bold</strong> for headlines and <strong>Open Sans</strong> for body copy.</p>
            </div>
          </div>
        </div>

        {/* 04 LOGO */}
        <div className="bg-section" id="logo">
          <div className="bg-container">
            <div className="bg-eyebrow">04 — Logo Usage</div>
            <h2 className="bg-section-title">Logo System</h2>
            <p className="bg-section-intro">The Digital Media Ninja mark consists of the ninja head icon + "DIGITAL MEDIA" in teal and "NINJA" in white with stylized lettering. Both elements must appear together unless written permission allows icon-only use.</p>
            <div className="bg-logo-grid">
              <div className="bg-logo-card dark-bg">
                <div className="bg-logo-label">Primary — Dark Background</div>
                <div className="bg-logo-text" style={{color:"#2ABFBF"}}>DIGITAL MEDIA<br/><span style={{color:"#fff"}}>NINJA</span></div>
                <div className="bg-logo-name">Use on black (#0A0A0A) or charcoal (#2C3140) backgrounds</div>
              </div>
              <div className="bg-logo-card light-bg">
                <div className="bg-logo-label" style={{color:"#2ABFBF"}}>Approved — Light Background</div>
                <div className="bg-logo-text" style={{color:"#2ABFBF"}}>DIGITAL MEDIA<br/><span style={{color:"#2C3140"}}>NINJA</span></div>
                <div className="bg-logo-name">Use on white or off-white backgrounds with dark NINJA text</div>
              </div>
              <div className="bg-logo-card dark-bg" style={{background:"#1a2535"}}>
                <div className="bg-logo-label">Approved — Dark Navy</div>
                <div className="bg-logo-text" style={{color:"#2ABFBF"}}>DIGITAL MEDIA<br/><span style={{color:"#fff"}}>NINJA</span></div>
                <div className="bg-logo-name">Acceptable on dark navy and dark slate backgrounds</div>
              </div>
              <div className="bg-logo-card teal-bg">
                <div className="bg-logo-label">Use Sparingly</div>
                <div className="bg-logo-text" style={{color:"#fff"}}>DIGITAL MEDIA<br/>NINJA</div>
                <div className="bg-logo-name">White logo on teal — only for specific branded materials</div>
              </div>
            </div>

            <h3 style={{fontFamily:"'Space Grotesk',sans-serif", fontSize:"20px", fontWeight:700, color:"#0A0A0A", marginBottom:"20px"}}>Logo Don'ts</h3>
            <div className="bg-donts-grid">
              <div className="bg-dont-card">
                <h4>Don't Recolor</h4>
                <p>Never change "DIGITAL MEDIA" from teal (#2ABFBF) to any other color.</p>
              </div>
              <div className="bg-dont-card">
                <h4>Don't Stretch</h4>
                <p>Always maintain the logo's original aspect ratio. No disproportionate scaling.</p>
              </div>
              <div className="bg-dont-card">
                <h4>Don't Add Effects</h4>
                <p>No drop shadows, gradients, glows, outlines, or bevel effects on the logo.</p>
              </div>
              <div className="bg-dont-card">
                <h4>Don't Separate Elements</h4>
                <p>The ninja icon and wordmark must always appear together as a unit.</p>
              </div>
              <div className="bg-dont-card">
                <h4>Don't Use on Busy Backgrounds</h4>
                <p>No placement on complex patterns, photos without a solid overlay, or low-contrast backgrounds.</p>
              </div>
              <div className="bg-dont-card">
                <h4>Don't Use Below Minimum Size</h4>
                <p>Digital: 200px wide minimum. Print: 1.5" wide minimum. Smaller sizes distort detail.</p>
              </div>
            </div>
            <div className="bg-divider" />
            <div className="bg-about-card">
              <h3>Clear Space Rule</h3>
              <p>Always maintain a minimum clear space equal to the height of the "N" in NINJA on all four sides of the logo. No other text, images, or graphical elements should encroach on this space.</p>
            </div>
          </div>
        </div>

        {/* 05 VOICE & TONE */}
        <div className="bg-section" id="voice">
          <div className="bg-container">
            <div className="bg-eyebrow">05 — Brand Voice &amp; Tone</div>
            <h2 className="bg-section-title">How We Sound</h2>
            <p className="bg-section-intro">Digital Media Ninja communicates like the smartest person in the room who actually wants you to understand — not to be impressed by their jargon.</p>
            <div className="bg-voice-grid">
              <div className="bg-voice-card">
                <div className="bg-voice-trait">Expert, Not Arrogant</div>
                <p>We know our craft deeply and show it through insights, not credentials. We educate clients rather than mystify them.</p>
              </div>
              <div className="bg-voice-card">
                <div className="bg-voice-trait">Direct &amp; Clear</div>
                <p>We say what we mean. No marketing fluff, no vague promises. Every claim is backed by strategy or data.</p>
              </div>
              <div className="bg-voice-card">
                <div className="bg-voice-trait">Bold &amp; Confident</div>
                <p>We take positions. We don't hedge everything. If a tactic doesn't work anymore, we'll say it plainly.</p>
              </div>
              <div className="bg-voice-card">
                <div className="bg-voice-trait">Forward-Thinking</div>
                <p>We talk about where search is going — AEO, GEO, AI visibility — not just where it's been. Clients trust us to see around corners.</p>
              </div>
              <div className="bg-voice-card">
                <div className="bg-voice-trait">Approachable</div>
                <p>We're sharp, not cold. A little personality is welcome. We're professional but we don't write like a press release.</p>
              </div>
              <div className="bg-voice-card">
                <div className="bg-voice-trait">Results-Obsessed</div>
                <p>Every piece of content, every campaign, every conversation comes back to outcomes. Traffic, leads, rankings, revenue.</p>
              </div>
            </div>
            <div className="bg-voice-compare">
              <div className="bg-vc-card do">
                <h4>Write Like This</h4>
                <ul>
                  <li>"AI search is changing how customers find you — here's how to stay visible."</li>
                  <li>"We'll audit your site and tell you exactly what's holding your rankings back."</li>
                  <li>"Local SEO isn't just Google Maps anymore — it's who shows up in ChatGPT's answer."</li>
                  <li>"Your competitors are investing in GEO. Here's why you should too."</li>
                  <li>"Get a free SEO audit and find out where you're leaving traffic on the table."</li>
                </ul>
              </div>
              <div className="bg-vc-card dont">
                <h4>Avoid This</h4>
                <ul>
                  <li>"Synergize your digital footprint with our holistic 360 solutions ecosystem."</li>
                  <li>"We leverage cutting-edge proprietary algorithms to maximize ROI potential."</li>
                  <li>"Our team of world-class ninjas are passionate about your success journey."</li>
                  <li>"Results may vary. We cannot guarantee rankings or traffic increases."</li>
                  <li>"Please do not hesitate to contact us at your earliest convenience."</li>
                </ul>
              </div>
            </div>
            <div className="bg-divider" />
            <div className="bg-about-grid">
              <div className="bg-about-card">
                <h3>Tone by Context</h3>
                <p><strong>Website / Landing Pages:</strong> Confident, direct, benefit-led.<br/><br/><strong>Proposals / Reports:</strong> Professional and precise, but still clear — avoid over-jargoning.<br/><br/><strong>Social Media:</strong> Slightly looser, educational, and personality-forward.<br/><br/><strong>Email Outreach:</strong> Conversational, personalized, low-pressure.</p>
              </div>
              <div className="bg-about-card">
                <h3>Words We Own</h3>
                <p>Use these terms consistently and with confidence across all content:</p><br/>
                <span className="bg-tag">AEO</span>
                <span className="bg-tag">GEO</span>
                <span className="bg-tag">AI Search Visibility</span>
                <span className="bg-tag">LLM Optimization</span>
                <span className="bg-tag">Local SEO</span>
                <span className="bg-tag">Answer Engine</span>
                <span className="bg-tag">Schema Markup</span>
                <span className="bg-tag">Search Dominance</span>
              </div>
            </div>
          </div>
        </div>

        {/* 06 SERVICES */}
        <div className="bg-section" id="services">
          <div className="bg-container">
            <div className="bg-eyebrow">06 — Service Offerings</div>
            <h2 className="bg-section-title">What We Do</h2>
            <p className="bg-section-intro">When writing about our services, always frame them around client outcomes — not process. Lead with the result, follow with the method.</p>
            <div className="bg-services-grid">
              {[
                { icon: "🎯", title: "SEO (Search Engine Optimization)", desc: "Technical audits, on-page optimization, keyword strategy, link building, and local SEO. We build the organic foundation that sustains long-term rankings across competitive markets." },
                { icon: "🤖", title: "AEO — Answer Engine Optimization", desc: "Optimizing content to be selected, cited, and summarized by AI systems including ChatGPT, Perplexity, and Google AI Overviews. The next frontier of search visibility." },
                { icon: "✨", title: "GEO — Generative Engine Optimization", desc: "Structuring and enriching content so it appears inside AI-generated answers. We position clients as the cited authority when AI tools respond to high-intent queries." },
                { icon: "📍", title: "Local SEO", desc: "Google Business Profile optimization, local citation building, map pack domination, and geo-targeted content. We put local businesses at the top of every local search that matters." },
                { icon: "🌐", title: "Web Design & Development", desc: "WordPress and custom website builds optimized for speed, mobile performance, and Core Web Vitals. Every site we build is designed to rank and convert." },
                { icon: "📈", title: "Google Ads / PPC", desc: "Data-driven paid search campaigns that complement organic strategy. We manage budgets with precision and optimize for real business outcomes — leads, calls, and revenue." },
                { icon: "🎬", title: "Video Marketing & Social", desc: "YouTube strategy, short-form video scripts, social content, and Instagram Reels production guidance. We extend brand reach across every channel where attention lives." },
                { icon: "⚙️", title: "AI Agent Deployment", desc: "Done-for-you AI operator setup for businesses looking to automate client touchpoints and lead nurturing. We deploy and configure AI agents tailored to each business workflow." },
                { icon: "📊", title: "SEO Audits & Content Strategy", desc: "Comprehensive site audits with actionable recommendations, competitor gap analysis, schema implementation, and editorial content calendars built around AEO and GEO targets." }
              ].map((s, i) => (
                <div key={i} className="bg-service-card">
                  <div className="bg-service-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 07 PERSONAS */}
        <div className="bg-section" id="personas">
          <div className="bg-container">
            <div className="bg-eyebrow">07 — Target Audience</div>
            <h2 className="bg-section-title">Who We Serve</h2>
            <p className="bg-section-intro">Understanding our client personas helps every team member and freelancer tailor copy, design, and strategy to the right audience. Always write for the person, not the industry.</p>
            <div className="bg-persona-grid">
              {/* Persona 1 */}
              <div className="bg-persona-card">
                <div className="bg-persona-header">
                  <div className="bg-persona-avatar">&#9878;&#65039;</div>
                  <div>
                    <div className="bg-persona-name">The Local Business Owner</div>
                    <div className="bg-persona-role">Law Firms &middot; Contractors &middot; Home Services &middot; Clinics</div>
                  </div>
                </div>
                <div className="bg-persona-body">
                  <h4>Who They Are</h4>
                  <p>A DFW-area small to mid-size business owner doing $500K–$5M/year. Busy, practical, and skeptical of agencies that promise everything and deliver reports.</p>
                  <h4>Pain Points</h4>
                  <ul>
                    <li>Not showing up in Google for their core services</li>
                    <li>Losing leads to competitors with better digital presence</li>
                    <li>Previous agency experiences that felt like black boxes</li>
                  </ul>
                  <h4>What They Want</h4>
                  <p>Transparency, clear ROI, and someone who speaks plain English about what's being done and why.</p>
                </div>
              </div>
              {/* Persona 2 */}
              <div className="bg-persona-card">
                <div className="bg-persona-header">
                  <div className="bg-persona-avatar">🏗️</div>
                  <div>
                    <div className="bg-persona-name">The Marketing Director</div>
                    <div className="bg-persona-role">Mid-Market &amp; Enterprise &middot; Manufacturing &middot; Multi-Location</div>
                  </div>
                </div>
                <div className="bg-persona-body">
                  <h4>Who They Are</h4>
                  <p>An internal marketing decision-maker at a company with $5M–$50M+ in revenue. Managing multiple brands or locations, juggling SEO, paid media, and brand consistency.</p>
                  <h4>Pain Points</h4>
                  <ul>
                    <li>Fragmented SEO across multiple sites or brands</li>
                    <li>Lack of AI search strategy — not ranking in Overviews</li>
                    <li>Need for detailed reporting and audit-level documentation</li>
                  </ul>
                  <h4>What They Want</h4>
                  <p>A strategic partner who delivers structured deliverables, competitive analysis, and a forward-looking search strategy.</p>
                </div>
              </div>
              {/* Persona 3 */}
              <div className="bg-persona-card">
                <div className="bg-persona-header">
                  <div className="bg-persona-avatar">💊</div>
                  <div>
                    <div className="bg-persona-name">The Wellness Brand</div>
                    <div className="bg-persona-role">Fitness &middot; IV Therapy &middot; Biohacking &middot; Supplements</div>
                  </div>
                </div>
                <div className="bg-persona-body">
                  <h4>Who They Are</h4>
                  <p>A founder or CMO at a health-forward DTC or service brand looking to grow through organic content, social, and SEO. Often has a community-first audience and needs credibility.</p>
                  <h4>Pain Points</h4>
                  <ul>
                    <li>Content that doesn't rank or drive conversions</li>
                    <li>Competing against big brands with big budgets</li>
                    <li>Needs E-E-A-T compliant content in a YMYL niche</li>
                  </ul>
                  <h4>What They Want</h4>
                  <p>SEO-optimized, science-backed content that builds authority and drives qualified organic traffic and product sales.</p>
                </div>
              </div>
              {/* Persona 4 */}
              <div className="bg-persona-card">
                <div className="bg-persona-header">
                  <div className="bg-persona-avatar">🏢</div>
                  <div>
                    <div className="bg-persona-name">The White-Label Partner</div>
                    <div className="bg-persona-role">Agencies &middot; Freelancers &middot; Web Studios</div>
                  </div>
                </div>
                <div className="bg-persona-body">
                  <h4>Who They Are</h4>
                  <p>A design or web agency needing to offer SEO and AI search services to their own clients. Looking for a reliable execution partner they can trust to deliver quietly and correctly.</p>
                  <h4>Pain Points</h4>
                  <ul>
                    <li>Clients asking about AI search they can't yet answer</li>
                    <li>Need for white-label-ready deliverables (reports, audits)</li>
                    <li>Want a partner, not a competitor for their clients</li>
                  </ul>
                  <h4>What They Want</h4>
                  <p>Reliable execution, professional reporting, and a partner who enhances their offering without stepping on their client relationships.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="bg-footer-inner">
          <p style={{fontFamily:"'Space Grotesk',sans-serif", fontSize:"20px", fontWeight:700, color:"#fff", marginBottom:"12px"}}>Digital Media Ninja</p>
          <p>Dallas, TX &middot; <strong>digitalmedianinja.com</strong></p>
          <p style={{marginTop:"16px", fontSize:"12px", color:"#555"}}>This document is confidential and intended for authorized team members, freelancers, and partners of Digital Media Ninja. Version 1.0 — April 2026.</p>
        </div>
      </div>
    </>
  );
};

export default BrandGuidelinesPage;
